import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { inr } from "../lib/api";
import { CheckCircle2, Truck, MessageCircle } from "lucide-react";

export default function OrderConfirmation() {
    const { id } = useParams();
    const [order, setOrder] = useState(null);

    useEffect(() => {
        document.title = "Order Confirmation | Loomara";
        api.get(`/orders/${id}`).then(({ data }) => setOrder(data)).catch(() => setOrder(false));
    }, [id]);

    if (order === null) return <div className="container-boutique py-20 text-center text-ink-500">Loading order…</div>;
    if (order === false) return (
        <div className="container-boutique py-20 text-center">
            <h1 className="font-serif text-4xl text-ink-900">Order not found</h1>
            <Link to="/" className="btn-primary mt-6 inline-flex">Back to Loomara</Link>
        </div>
    );

    return (
        <div className="container-boutique py-12" data-testid="order-confirmation-page">
            <div className="text-center">
                <CheckCircle2 size={42} className="mx-auto text-sage-500" strokeWidth={1.2} />
                <p className="eyebrow mt-4">Thank You</p>
                <h1 className="font-serif text-5xl text-ink-900 mt-3">Your order is on its way ♥</h1>
                <p className="text-ink-500 mt-3">Order <strong className="text-ink-900">#{order.orderNumber}</strong> · An email confirmation is on its way to {order.customerEmail}.</p>
            </div>

            <div className="grid lg:grid-cols-3 gap-6 mt-12">
                <div className="lg:col-span-2 card-soft p-6">
                    <h3 className="font-serif text-2xl text-ink-900">Order Details</h3>
                    <div className="divide-y divide-sage-300/20 mt-4">
                        {order.items.map((i, idx) => (
                            <div key={idx} className="py-3 flex gap-4">
                                <div className="h-16 w-14 rounded-xl bg-ivory-100 overflow-hidden">
                                    <img src={i.image} alt={i.name} className="w-full h-full object-cover" />
                                </div>
                                <div className="flex-1">
                                    <Link to={`/product/${i.slug}`} className="text-ink-900 hover:text-sage-700">{i.name}</Link>
                                    <p className="text-xs text-ink-500 mt-0.5">Size: {i.size || "—"} · Qty: {i.qty}</p>
                                </div>
                                <p className="text-sage-700">{inr(i.price * i.qty)}</p>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="card-soft p-6 space-y-5">
                    <div>
                        <p className="text-xs uppercase tracking-[0.22em] text-ink-500">Payment Summary</p>
                        <div className="text-sm mt-3 space-y-1.5">
                            <p className="flex justify-between text-ink-700"><span>Subtotal</span><span>{inr(order.subtotal)}</span></p>
                            <p className="flex justify-between text-ink-700"><span>Shipping</span><span>{order.shipping === 0 ? "Free" : inr(order.shipping)}</span></p>
                            <p className="flex justify-between text-ink-900 pt-2 text-lg font-serif"><span>Total</span><span>{inr(order.total)}</span></p>
                        </div>
                    </div>
                    <div>
                        <p className="text-xs uppercase tracking-[0.22em] text-ink-500 flex items-center gap-2"><Truck size={14} /> Ships to</p>
                        <p className="text-sm text-ink-700 mt-2 leading-relaxed">
                            {order.shippingAddress?.line1}<br/>
                            {order.shippingAddress?.line2 && <>{order.shippingAddress.line2}<br/></>}
                            {order.shippingAddress?.city}, {order.shippingAddress?.state} — {order.shippingAddress?.pincode}
                        </p>
                    </div>
                </div>
            </div>

            <div className="text-center mt-12">
                <Link to="/shop" className="btn-secondary mr-3">Continue Shopping</Link>
                <Link to="/account" className="btn-primary">View My Orders</Link>
            </div>
        </div>
    );
}
